import type { ActionId, KeyBindings } from './engineRuntimeStore';

const SETTINGS_KEY = 'sstk-engine:runtime-settings';
const DEBOUNCE_MS = 400;

export interface EngineSettings {
  mouseSensitivity: number;
  cameraModePref: 'first' | 'third';
  invertY: boolean;
  fov: number;
  headBob: boolean;
  moveSpeedMultiplier: number;
  keyBindings: KeyBindings;
  masterVolume: number;
  bloomIntensity: number;
  vignetteAmount: number;
  grainOpacity: number;
  chromaticAberration: boolean;
}

function clamp01(value: number) {
  return Math.min(1, Math.max(0, value));
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function parseKeyBindings(value: unknown, defaults: KeyBindings): KeyBindings {
  if (!value || typeof value !== 'object') return defaults;
  const parsed = value as Partial<Record<ActionId, unknown>>;
  const result = { ...defaults };
  for (const action of Object.keys(defaults) as ActionId[]) {
    const code = parsed[action];
    if (typeof code === 'string' && code.length > 0) result[action] = code;
  }
  return result;
}

/**
 * Reads persisted engine settings from localStorage, validating and clamping
 * every field independently against `defaults` (the same ranges the store's
 * own setters clamp to). Never throws and never returns a partial object -
 * a missing key, a malformed blob, or an unavailable localStorage (private
 * mode, quota) all fall back to `defaults` field-by-field. This module is
 * shared engine infrastructure: any game importing the runtime store gets
 * this behaviour for free under its own namespaced key, distinct from any
 * per-game settings key.
 */
export function loadEngineSettings(defaults: EngineSettings): EngineSettings {
  let raw: unknown;
  try {
    const stored = typeof window !== 'undefined' && window.localStorage ? window.localStorage.getItem(SETTINGS_KEY) : null;
    raw = stored ? JSON.parse(stored) : null;
  } catch {
    return defaults;
  }
  if (!raw || typeof raw !== 'object') return defaults;
  const parsed = raw as Partial<Record<keyof EngineSettings, unknown>>;

  return {
    mouseSensitivity: isFiniteNumber(parsed.mouseSensitivity) ? clamp01(parsed.mouseSensitivity) : defaults.mouseSensitivity,
    cameraModePref: parsed.cameraModePref === 'first' || parsed.cameraModePref === 'third'
      ? parsed.cameraModePref
      : defaults.cameraModePref,
    invertY: typeof parsed.invertY === 'boolean' ? parsed.invertY : defaults.invertY,
    fov: isFiniteNumber(parsed.fov) ? Math.min(110, Math.max(40, parsed.fov)) : defaults.fov,
    headBob: typeof parsed.headBob === 'boolean' ? parsed.headBob : defaults.headBob,
    moveSpeedMultiplier: isFiniteNumber(parsed.moveSpeedMultiplier)
      ? Math.min(1.2, Math.max(0.6, parsed.moveSpeedMultiplier))
      : defaults.moveSpeedMultiplier,
    keyBindings: parseKeyBindings(parsed.keyBindings, defaults.keyBindings),
    masterVolume: isFiniteNumber(parsed.masterVolume) ? clamp01(parsed.masterVolume) : defaults.masterVolume,
    bloomIntensity: isFiniteNumber(parsed.bloomIntensity) ? clamp01(parsed.bloomIntensity) : defaults.bloomIntensity,
    vignetteAmount: isFiniteNumber(parsed.vignetteAmount) ? clamp01(parsed.vignetteAmount) : defaults.vignetteAmount,
    grainOpacity: isFiniteNumber(parsed.grainOpacity) ? clamp01(parsed.grainOpacity) : defaults.grainOpacity,
    chromaticAberration: typeof parsed.chromaticAberration === 'boolean'
      ? parsed.chromaticAberration
      : defaults.chromaticAberration,
  };
}

function writeNow(settings: EngineSettings): void {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
    }
  } catch {
    // Best effort: localStorage can be unavailable (private mode, quota).
  }
}

let saveTimer: ReturnType<typeof setTimeout> | null = null;

/** Debounced write - coalesces rapid-fire slider drags into one write. */
export function saveEngineSettings(settings: EngineSettings): void {
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    saveTimer = null;
    writeNow(settings);
  }, DEBOUNCE_MS);
}

/** Writes immediately, bypassing the debounce (e.g. on pagehide). */
export function flushEngineSettings(settings: EngineSettings): void {
  if (saveTimer) {
    clearTimeout(saveTimer);
    saveTimer = null;
  }
  writeNow(settings);
}
